import PrimaryButton from "components/buttons/primary-button";
import ToggleAudio from "components/toggleAudio";
import Typography from "components/typography";
import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { Colors } from "utils/colors";
import { FormatAudioDuration } from "utils/dateConvert";

interface IProps {
  id?: string;
  no?: string;
  audio?: string;
  title?: string;
  artist?: string;
  isrc?: string;
  type?: string;
}

const TrackItem = ({ no, artist, title, audio, isrc, id, type }: IProps) => {
  const navigate = useNavigate();
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const handleLoadedMetadata = (event: any) => {
    const audioDuration = event.target.duration;

    setDuration(audioDuration);
  };

  const handleDownload = async () => {
    if (!audio) return;

    try {
      const response = await fetch(audio);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${title || "track"}.mp3`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error downloading audio:", error);
    }
  };

  interface LinkProps {
    active: boolean;
    onClick: () => void;
    text: string;
    download?: boolean;
  }
  const LinkItem = ({ active, onClick, text, download }: LinkProps) => {
    return (
      <LinkComponent
        isactive={active}
        onClick={() => {
          if (active || download) {
            onClick();
          }
        }}
      >
        <LinkIcon>
          {download ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="100%"
              height="100%"
              viewBox="0 0 14 15"
              fill="none"
            >
              <path
                d="M1.75 9.25C1.75 10.8999 1.75 11.7248 2.26256 12.2374C2.77513 12.75 3.60008 12.75 5.25 12.75H8.75C10.3999 12.75 11.2248 12.75 11.7374 12.2374C12.25 11.7248 12.25 10.8999 12.25 9.25"
                stroke="#5AB400"
                stroke-width="0.875"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M6.99935 2.25V9.83333M6.99935 9.83333L9.33268 7.28125M6.99935 9.83333L4.66602 7.28125"
                stroke="#5AB400"
                stroke-width="0.875"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          ) : active ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="100%"
              height="100%"
              viewBox="0 0 14 15"
              fill="none"
            >
              <path
                d="M6.99935 13.3332C10.221 13.3332 12.8327 10.7215 12.8327 7.49984C12.8327 4.27818 10.221 1.6665 6.99935 1.6665C3.77769 1.6665 1.16602 4.27818 1.16602 7.49984C1.16602 10.7215 3.77769 13.3332 6.99935 13.3332Z"
                stroke="#5AB400"
                stroke-width="0.875"
              />
              <path
                d="M4.95898 7.7915L6.12565 8.95817L9.04232 6.0415"
                stroke="#5AB400"
                stroke-width="0.875"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="100%"
              height="100%"
              viewBox="0 0 14 15"
              fill="none"
            >
              <path
                d="M6.99935 13.3332C10.221 13.3332 12.8327 10.7215 12.8327 7.49984C12.8327 4.27818 10.221 1.6665 6.99935 1.6665C3.77769 1.6665 1.16602 4.27818 1.16602 7.49984C1.16602 10.7215 3.77769 13.3332 6.99935 13.3332Z"
                stroke="#909090"
                stroke-width="0.875"
              />
              <path
                d="M8.45769 6.04152L5.54103 8.95817M5.54102 6.0415L8.45769 8.95817"
                stroke="#909090"
                stroke-width="0.875"
                stroke-linecap="round"
              />
            </svg>
          )}
        </LinkIcon>
        <Typography decoration="underline" size="s" color={Colors.grey100}>
          {text}
        </Typography>
      </LinkComponent>
    );
  };

  //empty tr acts as a space
  return (
    <>
      <tr></tr>
      <tr>
        <td>
          <Typography ml="2" size="s">
            {no}
          </Typography>
        </td>
        <td>
          <ToggleAudio file={audio} />
          <Typography size="s" mt="0.4" color={Colors.grey500}>
            {FormatAudioDuration(duration)}
          </Typography>
          <audio
            ref={audioRef}
            onLoadedMetadata={handleLoadedMetadata}
            style={{ display: "none" }}
          >
            <source src={audio} type="audio/mpeg" />
            Your browser does not support the audio element.
          </audio>
        </td>
        <td>
          <Typography size="s" weight="medium" mb="0.4">
            {title}
          </Typography>
          <Typography size="xs" color={Colors.grey500}>
            by {artist}
          </Typography>
        </td>
        <td>
          <LinkItem
            active={true}
            text="Plain Lyrics"
            onClick={() => {
              navigate(
                `/overview/discography/track-lyrics/${id}?tab=plain&type=${type}`
              );
            }}
          />
        </td>
        <td>
          <LinkItem
            active={true}
            text="Sync Lyrics"
            onClick={() => {
              navigate(
                `/overview/discography/track-lyrics/${id}?tab=sync&type=${type}`
              );
            }}
          />
        </td>
        <td>
          <LinkItem
            active={true}
            text="Credits"
            onClick={() => {
              navigate(
                `/overview/discography/track-lyrics/${id}?tab=credits&type=${type}`
              );
            }}
          />
        </td>
        <td>
          <LinkItem
            active={true}
            text="Download"
            onClick={handleDownload}
            download
          />
        </td>
        <td>
          <Typography size="s" color={Colors.grey100}>
            {isrc}
          </Typography>
        </td>
        <td>
          <EditNicon>
            <PrimaryButton
              onClick={() => {
                navigate(`/overview/edit-metadata/${id}?type=${type}`);
              }}
              variant="text"
              text="Edit"
              iconPosition="left"
              iconSize={"1.6"}
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="100%"
                  height="100%"
                  viewBox="0 0 14 15"
                  fill="none"
                >
                  <path
                    d="M8.37803 2.87906L8.91866 2.33838C9.81448 1.44255 11.2669 1.44255 12.1628 2.33838C13.0586 3.2342 13.0586 4.68663 12.1628 5.58246L11.6221 6.12313M8.37803 2.87906C8.37803 2.87906 8.44558 4.028 9.45935 5.04178C10.4731 6.05555 11.6221 6.12313 11.6221 6.12313M8.37803 2.87906L3.40728 7.84978C3.0706 8.18648 2.90226 8.35477 2.75749 8.54039C2.58671 8.75937 2.4403 8.99626 2.32083 9.24692C2.21956 9.45943 2.14428 9.6853 1.99371 10.137L1.35569 12.0511M11.6221 6.12313L6.65136 11.0939C6.31466 11.4305 6.14637 11.5989 5.96075 11.7436C5.74178 11.9144 5.50487 12.0609 5.25421 12.1803C5.04171 12.2816 4.81585 12.3568 4.36416 12.5074L2.45009 13.1455M1.35569 12.0511L1.19973 12.519C1.12564 12.7412 1.18349 12.9863 1.34917 13.152C1.51485 13.3177 1.75992 13.3755 1.98221 13.3014L2.45009 13.1455M1.35569 12.0511L2.45009 13.1455"
                    stroke="currentColor"
                    stroke-width="1.02083"
                  />
                </svg>
              }
            />
            <TrackIcon>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="100%"
                height="100%"
                viewBox="0 0 24 25"
                fill="none"
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M22 12.5C22 18.0228 17.5228 22.5 12 22.5C6.47715 22.5 2 18.0228 2 12.5C2 6.97715 6.47715 2.5 12 2.5C17.5228 2.5 22 6.97715 22 12.5ZM16.0303 9.46967C16.3232 9.76256 16.3232 10.2374 16.0303 10.5303L11.0303 15.5303C10.7374 15.8232 10.2626 15.8232 9.96967 15.5303L7.96967 13.5303C7.67678 13.2374 7.67678 12.7626 7.96967 12.4697C8.26256 12.1768 8.73744 12.1768 9.03033 12.4697L10.5 13.9393L12.7348 11.7045L14.9697 9.46967C15.2626 9.17678 15.7374 9.17678 16.0303 9.46967Z"
                  fill="#5AB400"
                />
              </svg>
            </TrackIcon>
          </EditNicon>
        </td>
      </tr>
    </>
  );
};

export default TrackItem;

const LinkComponent = styled.div<{ isactive: boolean }>`
  display: flex;
  align-items: center;
  cursor: ${(props) => (props.isactive ? "pointer" : "not-allowed")};
`;

const LinkIcon = styled.div`
  width: 1.4rem;
  height: 1.4rem;
  margin-right: 0.4rem;
`;

const EditNicon = styled.div`
  display: grid;
  grid-template-columns: 8rem 10rem;
  align-items: center;
`;

const TrackIcon = styled.div`
  width: 2.4rem;
  height: 2.4rem;
`;
